import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, ContactButton, Icon, SocialLinks } from '../../components/common';
import { DatePicker, Input, Select } from '../../components/forms';
import { PageHeader } from '../../components/layout';
import { CONTACT, EVENT_TYPES, IMAGES, SOCIAL_LINKS } from '../../constants';

// Web3Forms access key from environment variable
// For local dev: add to .env file as REACT_APP_WEB3FORMS_ACCESS_KEY=your_key
// For Netlify: add in Site Settings > Environment Variables
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '';

/**
 * Enquiry Page - Booking form and contact options
 */
const Enquiry = () => {
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get('type') || '';

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    eventType: preselectedType,
    selectedDate: null,
    preferredTime: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    // Clear status message when user types
    if (submitStatus.message) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({
      ...prev,
      selectedDate: date,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.eventType || !formData.phone) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    // Check if access key is configured
    if (!WEB3FORMS_ACCESS_KEY) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Something went wrong! Please contact via WhatsApp or Phone.' 
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    // Format the date nicely
    const formattedDate = formData.selectedDate 
      ? formData.selectedDate.toLocaleDateString('en-IN', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      : 'Not specified';

    // Get event type label
    const eventTypeLabel = EVENT_TYPES.find(t => t.value === formData.eventType)?.label || formData.eventType;


    // Format time nicely
    const formattedTime = formData.preferredTime 
      ? new Date(`2000-01-01T${formData.preferredTime}`).toLocaleTimeString('en-IN', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
      : 'Not specified';

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `🎤 Booking Enquiry: ${eventTypeLabel} - ${formData.fullName}`,
          from_name: formData.fullName,
          // Form fields
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          event_type: eventTypeLabel,
          preferred_date: formattedDate,
          preferred_time: formattedTime,
          // Extra settings
          botcheck: false,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({ 
          type: 'success', 
          message: '🎉 Enquiry sent successfully! We\'ll get back to you soon.' 
        });
        // Reset form
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          eventType: '',
          selectedDate: null,
          preferredTime: '',
        });
      } else {
        throw new Error(result.message || 'Something went wrong');
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: `Failed to send: ${error.message}. Try WhatsApp instead!` 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Page-specific meta information
  const pageTitle = "Book a Show | Matisa - Singer & Guitarist";
  const pageDescription = "Book Matisa for your next event - weddings, corporate shows, private parties, and more. Quick response via WhatsApp or email.";

  return (
    <div className="flex h-screen-safe w-full flex-row overflow-hidden">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/enquiry" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Left Panel: Brand Visuals (Desktop Only) */}
      <div className="hidden lg:flex lg:flex-1 relative h-full bg-surface overflow-hidden items-center justify-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40 mix-blend-luminosity hover:mix-blend-normal hover:opacity-60 transition-all duration-700 hover:scale-105"
          style={{ backgroundImage: `url('${IMAGES.enquiryBackground}')` }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/50 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 p-16 max-w-2xl text-center flex flex-col items-center">
          <div className="mb-8 size-20 rounded-full border border-gold/30 flex items-center justify-center bg-black/20 backdrop-blur-md">
            <Icon name="mic_external_on" size="2xl" className="text-gold" />
          </div>
          <h1 className="text-7xl font-extrabold tracking-tight text-white mb-6 leading-none">
            <span className="text-gold-gradient">MATISA</span><br/>LIVE
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed font-light max-w-md">
            Unforgettable acoustic sessions and high-energy performances for your most exclusive events.
          </p>
        </div>
        
        {/* Location Tag */}
        <div className="absolute bottom-10 left-10 flex items-center gap-2 text-white/50 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10">
          <Icon name="location_on" size="xs" />
          <span className="text-xs uppercase tracking-widest font-semibold">Available Worldwide</span>
        </div>
      </div>

      {/* Right Panel: Mobile-Optimized Interaction Area */}
      <div className="flex-1 lg:max-w-[480px] w-full h-full flex flex-col bg-background-dark relative border-l border-[#223249] shadow-2xl z-20">
        {/* Header - Using PageHeader for consistent navigation */}
        <PageHeader title="Matisa" />

        {/* Main Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto scrollbar-hide pb-36">
          {/* Hero / Title Section */}
          <div className="p-6 pb-4 pt-8">
            <h2 className="text-3xl font-bold text-white mb-2 leading-tight">
              Secure Your <span className="text-gold">Date</span>
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              Let's make magic happen. Contact us directly or fill in the form below.
            </p>
          </div>

          <div className="flex items-center gap-4 px-6 py-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#2e3f57] to-transparent" />
            <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Quick Contact</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#2e3f57] to-transparent" />
          </div>

          {/* Quick Contact Section - Moved above form */}
          <div className="px-6 pb-4">
            <div className="grid grid-cols-2 gap-3">
              <ContactButton
                type="whatsapp"
                title="WhatsApp"
                subtitle="Fastest response"
                href={SOCIAL_LINKS.whatsapp}
                fullWidth
              />
              <ContactButton
                type="email"
                title="Email Us"
                href={`mailto:${CONTACT.email}`}
                fullWidth
              />
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 px-6 py-3">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#2e3f57] to-transparent" />
            <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Or Fill the Form</span>
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#2e3f57] to-transparent" />
          </div>

          {/* Booking Form */}
          <form className="px-6 py-4 space-y-5" onSubmit={handleSubmit}>
            {/* Name Field */}
            <Input
              label="Full Name"
              icon="person"
              placeholder="Enter your name"
              value={formData.fullName}
              onChange={handleInputChange('fullName')}
              required
            />

            {/* Contact Info Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Phone"
                icon="call"
                type="tel"
                placeholder="+91 9876..."
                value={formData.phone}
                onChange={handleInputChange('phone')}
                required
              />
              <Input
                label="Email"
                icon="mail"
                type="email"
                placeholder="Email..."
                value={formData.email}
                onChange={handleInputChange('email')}
              />
            </div>

            {/* Event Type Dropdown */}
            <Select
              label="Event Type"
              icon="piano"
              options={EVENT_TYPES}
              value={formData.eventType}
              onChange={handleInputChange('eventType')}
              placeholder="Select event type"
              required
            />

            {/* Date Picker */}
            <DatePicker
              label="Select Date"
              selectedDate={formData.selectedDate}
              onDateSelect={handleDateSelect}
            />

            {/* Preferred Time */}
            <Input
              label="Preferred Time"
              icon="schedule"
              type="time"
              placeholder="7:00 PM"
              value={formData.preferredTime}
              onChange={handleInputChange('preferredTime')}
            />

            {/* Status Message */}
            {submitStatus.message && (
              <div className={`p-4 rounded-xl text-sm font-medium ${
                submitStatus.type === 'success' 
                  ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                  : 'bg-red-500/10 text-red-400 border border-red-500/20'
              }`}>
                {submitStatus.message}
              </div>
            )}
          </form>

          {/* Compact Footer */}
          <div className="px-6 pb-8 text-center border-t border-[#223249] pt-6 mx-6">
            <SocialLinks className="mb-4" />
            <p className="text-[10px] text-gray-600 uppercase tracking-widest font-semibold">
              © {new Date().getFullYear()} Matisa Music
            </p>
          </div>
        </div>

        {/* Sticky Bottom Bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-background-dark/90 backdrop-blur-lg border-t border-[#223249] z-40">
          <Button
            variant="primary"
            size="lg"
            icon={isSubmitting ? null : "arrow_forward"}
            fullWidth
            shine
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Enquiry'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Enquiry;
